import { useCallback, useEffect, useRef, useState } from 'react';
import type { Location } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

interface NavItem {
  to: string;
  matchRoutes?: RegExp[];
}

const getSelected = (items: NavItem[], location: Location) => {
  // Modals have higher priority than routes
  if (location.hash !== '') {
    const index = items.findIndex((link) => link.to === location.hash);

    if (index !== -1) {
      return index;
    }
  }

  let foundIndex = items.findIndex((link) => link.to === location.pathname);

  // Check if the current route matches any of the matchRoutes
  if (foundIndex === -1) {
    foundIndex = items.findIndex(
      (link) =>
        link.matchRoutes?.some((route) => route.test(location.pathname)) ??
        false,
    );
  }

  // Fallback to the first match
  if (foundIndex === -1) {
    // Ignore the root route as that would always be selected
    foundIndex = items.findIndex(
      (link) => link.to !== '/' && location.pathname.startsWith(link.to),
    );
  }

  return foundIndex !== -1 ? foundIndex : undefined;
};

/**
 * Returns the index of the selected item based on the current location
 * @param items list of items that can be selected
 * @returns index of the selected item or undefined if none is matching
 */
export const useSelected = ({ items }: { items: NavItem[] }) => {
  const location = useLocation();
  const [selected, setSelected] = useState<number | undefined>(() =>
    getSelected(items, location),
  );
  const refToBlur = useRef<HTMLElement | null>(null);

  // This effect is used to highlight the active link in the sidebar
  useEffect(() => {
    setSelected(getSelected(items, location));
  }, [items, location]);

  useEffect(() => {
    refToBlur.current?.blur();
  }, [selected]);

  const setRefToBlur = useCallback((element: HTMLElement | null) => {
    refToBlur.current = element;
  }, []);

  return { selected, setRefToBlur };
};
